import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Users, Sparkles, ArrowRight, QrCode, Key, Camera, HelpCircle } from "lucide-react";
import QRScanner from "../components/QRScanner";
import { ModeToggle, useTheme } from "../context/ThemeContext";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { isDark, themeConfig } = useTheme();
  const [joinCode, setJoinCode] = useState("");
  const [facilitatorCode, setFacilitatorCode] = useState("");
  const [showFacilitatorReturn, setShowFacilitatorReturn] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleJoinWithCode = (e) => {
    e.preventDefault();
    if (joinCode.trim()) {
      navigate(`/join/${joinCode.trim().toUpperCase()}`);
    }
  };

  const handleQRScan = (scannedCode) => {
    setShowScanner(false);
    toast.success(`Code scanned: ${scannedCode}`);
    navigate(`/join/${scannedCode}`);
  };

  const handleFacilitatorReturn = async (e) => {
    e.preventDefault();
    if (!facilitatorCode.trim()) {
      toast.error("Please enter your facilitator code");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/sessions/facilitator/${facilitatorCode.trim().toUpperCase()}`);
      const session = response.data;
      toast.success(`Welcome back! Opening "${session.name}"`);
      navigate(`/facilitator/${session.id}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Session not found. Check your facilitator code.");
      } else {
        toast.error("Failed to find session");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Dark/Light Mode Toggle */}
      <div className="absolute top-4 right-4 z-20">
        <ModeToggle />
      </div>

      {/* Background decorations */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#E91E8C] rounded-full opacity-20 blur-[100px]" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#8B5CF6] rounded-full opacity-20 blur-[120px]" />
      
      {/* QR Scanner Overlay */}
      <AnimatePresence>
        {showScanner && (
          <QRScanner 
            onScan={handleQRScan} 
            onClose={() => setShowScanner(false)} 
          />
        )}
      </AnimatePresence>
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-lg w-full text-center relative z-10"
      >
        {/* Logo */}
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Logo size="md" />
          </div>
          <h1 className="logo-text text-4xl sm:text-5xl gradient-text mb-3">
            Carousel Workshop
          </h1>
          <p className="text-lg text-[#E2D9F3]">
            Powered by <span className="font-semibold text-white">Reshapers</span>
          </p>
        </motion.div>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-[#E2D9F3] text-lg mb-10 leading-relaxed"
        >
          Guide your team through interactive stations with live rotations, energizing activities, and clear direction.
        </motion.p>

        {/* Action Cards */}
        <div className="space-y-4">
          {/* Facilitator Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-heavy rounded-2xl p-6 text-left"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center shrink-0">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-xl text-white mb-1">I'm a Facilitator</h3>
                <p className="text-[#E2D9F3] text-sm mb-4">Create and manage workshop sessions</p>
                <div className="space-y-2">
                  <Button
                    data-testid="create-session-btn"
                    onClick={() => navigate("/create")}
                    className="w-full btn-primary-gradient control-btn"
                  >
                    Create New Session
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  
                  {!showFacilitatorReturn ? (
                    <button
                      onClick={() => setShowFacilitatorReturn(true)}
                      className="w-full text-sm text-[#E2D9F3] hover:text-white transition-colors flex items-center justify-center gap-2 py-2"
                    >
                      <Key className="w-4 h-4" />
                      Return to existing session
                    </button>
                  ) : (
                    <form onSubmit={handleFacilitatorReturn} className="mt-3 space-y-2">
                      <Input
                        data-testid="facilitator-code-input"
                        type="text"
                        placeholder="Enter facilitator code"
                        value={facilitatorCode}
                        onChange={(e) => setFacilitatorCode(e.target.value.toUpperCase())}
                        maxLength={8}
                        className="input-dark uppercase font-mono text-center tracking-widest"
                      />
                      <Button
                        type="submit"
                        disabled={isLoading || facilitatorCode.length < 6}
                        className="w-full btn-ghost"
                      >
                        {isLoading ? "Loading..." : "Open Session"}
                      </Button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Participant Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-heavy rounded-2xl p-6 text-left"
          >
            <div className="flex items-start gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: isDark ? '#CCFF00' : themeConfig?.colors?.primary || '#E91E8C' }}
              >
                <QrCode className={`w-6 h-6 ${isDark ? 'text-black' : 'text-white'}`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-xl text-white mb-1">I'm a Participant</h3>
                <p className="text-[#E2D9F3] text-sm mb-4">Join a session with a code</p>
                <form onSubmit={handleJoinWithCode} className="flex gap-2">
                  <Input
                    data-testid="join-code-input"
                    type="text"
                    placeholder="Enter code"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    maxLength={6}
                    className="input-dark flex-1 uppercase font-mono text-center tracking-widest"
                  />
                  <Button
                    data-testid="join-session-btn"
                    type="submit"
                    className="btn-action control-btn px-6"
                    disabled={joinCode.length < 4}
                  >
                    Join
                  </Button>
                </form>
                <button
                  data-testid="scan-qr-btn"
                  onClick={() => setShowScanner(true)}
                  className="mt-3 text-sm hover:text-white transition-colors flex items-center gap-2 mx-auto font-medium"
                  style={{ color: isDark ? '#CCFF00' : themeConfig?.colors?.primary || '#E91E8C' }}
                >
                  <Camera className="w-4 h-4" />
                  Scan QR Code
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Help Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-8"
        >
          <Link 
            to="/guide"
            className="inline-flex items-center gap-2 text-[#E2D9F3] hover:text-white transition-colors"
          >
            <HelpCircle className="w-5 h-5" />
            <span>How to use this app</span>
          </Link>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 text-sm text-[#6B5E7C]"
        >
          No account needed • Works on any device
        </motion.p>
      </motion.div>
    </div>
  );
};

export default LandingPage;
